import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import {
  CompetitionScorecard,
  Select,
  SelectItem,
  type ScorecardData,
} from '@structural-climbing/ui'
import { useGymContext } from '../../contexts/GymContext'
import { MemberIdentity } from '../members/MemberIdentity'
import { CompetitorProfileModal, type CompetitorTarget } from './CompetitorProfileModal'
import type { CompetitionCategory, CompetitionEntry } from './types'

interface Props {
  competitionId: string
  categories: CompetitionCategory[]
  /** For each competitor's email and, for a child's own login, the child. */
  entries: CompetitionEntry[]
  /** Changes whenever results reload, so a top entered at the desk shows here. */
  version: unknown
}

/**
 * The same table the climbers see on the site and in the app: every route
 * against every competitor in one category. For the desk, it is the answer to
 * "why do I have fewer points than him?".
 */
export function ScorecardTab({ competitionId, categories, entries, version }: Props) {
  const { t } = useTranslation()
  const { getAccessTokenSilently } = useAuth()
  const { primaryColor } = useGymContext()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [categoryId, setCategoryId] = useState<string | null>(categories[0]?.id ?? null)
  const [data, setData] = useState<ScorecardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const [profileTarget, setProfileTarget] = useState<CompetitorTarget | null>(null)

  const entryByUser = useMemo(() => new Map(entries.map((e) => [e.user_id, e])), [entries])

  const load = useCallback(async () => {
    if (!categoryId) return
    setLoading(true)
    setFailed(false)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(
        `${apiUrl}/competitions/${competitionId}/scorecard?category=${encodeURIComponent(categoryId)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      if (res.ok) setData((await res.json()) as ScorecardData)
      else setFailed(true)
    } catch {
      setFailed(true)
    } finally {
      setLoading(false)
    }
  }, [apiUrl, competitionId, categoryId, getAccessTokenSilently])

  useEffect(() => {
    void load()
  }, [load, version])

  if (!categoryId) {
    return <p className="py-10 text-center text-sm text-foreground/60">{t('scorecard.empty')}</p>
  }

  return (
    <div className="flex flex-col gap-3">
      {categories.length > 1 && (
        <div className="grid gap-3 sm:grid-cols-2">
          <Select
            label={t('scorecard.categoryLabel')}
            size="sm"
            selectedKeys={[categoryId]}
            disallowEmptySelection
            onSelectionChange={(keys) => {
              const next = [...keys][0]
              if (next) setCategoryId(String(next))
            }}
          >
            {categories.map((c) => (
              <SelectItem key={c.id}>{c.name}</SelectItem>
            ))}
          </Select>
        </div>
      )}

      <CompetitionScorecard
        // A table from the previous category must not flash under the new one.
        data={data?.categoryId === categoryId ? data : null}
        loading={loading}
        failed={failed}
        renderClimber={(climber) => {
          const entry = entryByUser.get(climber.userId)
          return (
            <MemberIdentity
              userId={climber.userId}
              name={climber.fullName || climber.displayName}
              nickname={climber.nickname}
              email={entry?.profile?.email}
              avatarExt={climber.avatarExt}
              avatarUpdatedAt={climber.avatarUpdatedAt}
              primaryColor={primaryColor}
              size="sm"
            />
          )
        }}
        onOpenClimber={(climber) =>
          setProfileTarget({
            userId: climber.userId,
            childId: entryByUser.get(climber.userId)?.child_id ?? null,
          })
        }
      />

      {profileTarget && (
        <CompetitorProfileModal target={profileTarget} onClose={() => setProfileTarget(null)} />
      )}
    </div>
  )
}
